import { cn } from "@/utils";
import { forwardRef, type ButtonHTMLAttributes } from "react";

type HermesButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "outline";
  size?: "sm" | "md";
  /** Só ícone: quadrado, sem texto. Exige `aria-label`. */
  iconOnly?: boolean;
};

const VARIANT = {
  primary:
    "bg-hermes-primary text-hermes-primary-fg hover:opacity-[.88] disabled:opacity-50",
  ghost:
    "text-hermes-muted-fg hover:bg-hermes-muted hover:text-hermes-fg disabled:opacity-50",
  outline:
    "border border-hermes-border bg-hermes-bg text-hermes-fg hover:bg-hermes-muted disabled:opacity-50",
} as const;

/**
 * Botão do Hermes. `group` já vem ligado para o ícone dentro dele reagir ao
 * hover (ver `HermesIcon`). Alturas: sm = 36px, md = 48px; só-ícone = 36px.
 */
export const HermesButton = forwardRef<HTMLButtonElement, HermesButtonProps>(
  (
    {
      variant = "ghost",
      size = "sm",
      iconOnly,
      className,
      type = "button",
      ...props
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        "group hermes-focus-ring inline-flex shrink-0 cursor-pointer items-center justify-center gap-2",
        "rounded-[0.7rem] font-semibold whitespace-nowrap",
        "transition duration-150 active:scale-[.98] disabled:cursor-not-allowed",
        "motion-reduce:transition-none",
        size === "sm" ? "h-9 text-sm" : "h-12 text-base",
        iconOnly
          ? size === "sm"
            ? "size-9"
            : "size-12"
          : size === "sm"
            ? "px-3.5"
            : "px-[1.375rem]",
        VARIANT[variant],
        className
      )}
      {...props}
    />
  )
);
HermesButton.displayName = "HermesButton";
